import { useState } from "react";
import { useForm } from "react-hook-form";
import { CheckCircle, AlertCircle, Loader2, Send } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const serviceOptions = [
  "Screen Printing", "DTF Printing", "Embroidery", "Custom Jerseys",
  "Specialty Printing", "Graphic Design", "Not Sure — Help Me Decide",
];
const quantityOptions = [
  "1-11 pieces", "12-23 pieces", "24-47 pieces", "48-71 pieces",
  "72-143 pieces", "144-287 pieces", "288+ pieces",
];
const designOptions = [
  "I have print-ready artwork (AI, EPS, PDF)",
  "I have a logo/image but need print prep",
  "I have a concept but need design help",
  "I need design from scratch",
];
const referralOptions = [
  "Google Search", "Bing / Yahoo", "Instagram", "Facebook",
  "Referred by a friend", "Repeat customer", "Other",
];

export default function QuoteForm({ compact = false }) {
  const [status, setStatus] = useState("idle");
  const [submittedName, setSubmittedName] = useState("");
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const onSubmit = async (data) => {
    setStatus("loading");
    try {
      await axios.post(`${BACKEND_URL}/api/leads`, data);
      setSubmittedName(data.full_name.split(" ")[0]);
      setStatus("success");
      reset();
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div data-testid="quote-form-success" className="flex flex-col items-center justify-center text-center py-12 px-6">
        <div className="w-16 h-16 bg-green-500/10 border border-green-500/30 flex items-center justify-center mb-4">
          <CheckCircle size={32} className="text-green-500" />
        </div>
        <h3 className="font-teko text-3xl text-white uppercase tracking-wide mb-2">Mission Received, {submittedName}!</h3>
        <p className="text-neutral-400 font-inter text-sm max-w-sm">
          Our team will review your order brief and contact you within 2 business hours with a detailed quote. Check your email!
        </p>
        <button
          onClick={() => setStatus("idle")}
          className="mt-6 text-sm text-red-500 hover:text-red-400 transition-colors uppercase tracking-widest font-inter"
        >
          Submit Another Request
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      data-testid="quote-form"
      className={`space-y-4 ${compact ? "" : ""}`}
    >
      {!compact && (
        <div className="mb-6">
          <div className="w-8 h-px bg-red-600 mb-3" />
          <h2 className="font-teko text-3xl sm:text-4xl text-white uppercase tracking-tight">Submit Your Order Brief</h2>
          <p className="text-neutral-400 text-sm font-inter mt-1">Free quote within 2 hours. No obligation.</p>
        </div>
      )}

      {/* Name + Email */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-neutral-500 uppercase tracking-widest font-inter block mb-1">Full Name *</label>
          <input
            data-testid="form-full-name"
            {...register("full_name", { required: "Name is required" })}
            className="ninja-input"
            placeholder="Jane Rodriguez"
          />
          {errors.full_name && <p className="text-red-500 text-xs mt-1">{errors.full_name.message}</p>}
        </div>
        <div>
          <label className="text-xs text-neutral-500 uppercase tracking-widest font-inter block mb-1">Email *</label>
          <input
            data-testid="form-email"
            type="email"
            {...register("email", { required: "Email is required", pattern: { value: /^\S+@\S+$/i, message: "Invalid email" } })}
            className="ninja-input"
            placeholder="jane@company.com"
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
        </div>
      </div>

      {/* Phone + Company */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-neutral-500 uppercase tracking-widest font-inter block mb-1">Phone Number *</label>
          <input
            data-testid="form-phone"
            {...register("phone", { required: "Phone is required" })}
            className="ninja-input"
            placeholder="(214) 555-0000"
          />
          {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
        </div>
        <div>
          <label className="text-xs text-neutral-500 uppercase tracking-widest font-inter block mb-1">Company / Organization</label>
          <input
            data-testid="form-company"
            {...register("company")}
            className="ninja-input"
            placeholder="Dallas Startup Inc."
          />
        </div>
      </div>

      {/* Service + Quantity */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-neutral-500 uppercase tracking-widest font-inter block mb-1">Service Needed *</label>
          <select
            data-testid="form-service-type"
            {...register("service_type", { required: "Please select a service" })}
            className="ninja-input appearance-none cursor-pointer"
          >
            <option value="">Select a service...</option>
            {serviceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {errors.service_type && <p className="text-red-500 text-xs mt-1">{errors.service_type.message}</p>}
        </div>
        <div>
          <label className="text-xs text-neutral-500 uppercase tracking-widest font-inter block mb-1">Quantity *</label>
          <select
            data-testid="form-quantity"
            {...register("quantity", { required: "Please select quantity" })}
            className="ninja-input appearance-none cursor-pointer"
          >
            <option value="">How many pieces?</option>
            {quantityOptions.map((q) => <option key={q} value={q}>{q}</option>)}
          </select>
          {errors.quantity && <p className="text-red-500 text-xs mt-1">{errors.quantity.message}</p>}
        </div>
      </div>

      {/* Needed By + Design Status */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-neutral-500 uppercase tracking-widest font-inter block mb-1">Needed By (Date)</label>
          <input
            data-testid="form-needed-by"
            type="date"
            {...register("needed_by")}
            className="ninja-input"
          />
        </div>
        <div>
          <label className="text-xs text-neutral-500 uppercase tracking-widest font-inter block mb-1">Design Status *</label>
          <select
            data-testid="form-design-status"
            {...register("design_status", { required: "Please select design status" })}
            className="ninja-input appearance-none cursor-pointer"
          >
            <option value="">Where is your design?</option>
            {designOptions.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          {errors.design_status && <p className="text-red-500 text-xs mt-1">{errors.design_status.message}</p>}
        </div>
      </div>

      {/* Message */}
      <div>
        <label className="text-xs text-neutral-500 uppercase tracking-widest font-inter block mb-1">Order Details / Message</label>
        <textarea
          data-testid="form-message"
          {...register("message")}
          rows={4}
          className="ninja-input resize-none"
          placeholder="Tell us about your order: garment type, colors, printing location, special requirements..."
        />
      </div>

      {/* Referral */}
      <div>
        <label className="text-xs text-neutral-500 uppercase tracking-widest font-inter block mb-1">How Did You Hear About Us?</label>
        <select
          data-testid="form-referral"
          {...register("referral_source")}
          className="ninja-input appearance-none cursor-pointer"
        >
          <option value="">Select one...</option>
          {referralOptions.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      {status === "error" && (
        <div className="flex items-center gap-2 text-red-400 text-sm bg-red-900/20 border border-red-900/40 p-3">
          <AlertCircle size={16} />
          <span>Something went wrong. Please try again or call us at (214) 555-0190.</span>
        </div>
      )}

      <button
        type="submit"
        data-testid="form-submit-btn"
        disabled={status === "loading"}
        className="w-full ninja-btn-primary flex items-center justify-center gap-2 py-4 text-lg font-teko tracking-widest disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {status === "loading" ? (
          <><Loader2 size={18} className="animate-spin" /> SUBMITTING...</>
        ) : (
          <><Send size={16} /> SUBMIT ORDER BRIEF — GET FREE QUOTE</>
        )}
      </button>

      <p className="text-center text-xs text-neutral-600 font-inter">
        We respond within 2 business hours. Free quote, no obligation.
      </p>
    </form>
  );
}
